import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useState, useEffect, useCallback } from 'react';
import { useHoverDirty, useClickAway } from 'react-use';
import '../../../constants/icons/generic/close.svg.mjs';
import '../../../contexts/MenuContext/index.mjs';
import { AvailableLanguages } from '../../../contexts/MenuContext/mapping.mjs';
import '../../../hooks/index.mjs';
import '../../StaticModal/index.mjs';
import '../../StyleableSvgImage/index.mjs';
import { Frame, HeadingContainer, Heading, Close, LanguageGrid, CustomLinkButton, LanguageValue } from './LanguageFrame.styles.mjs';
import './translations/index.mjs';
import { buildLanguageLink } from './utils/buildLanguageLink.mjs';
import { useMenu } from '../../../contexts/MenuContext/context.mjs';
import { useTranslation } from '../../../hooks/useTranslation.mjs';
import { Language } from '../../../types/constants.mjs';
import EN_TRANSLATION from './translations/en.json.mjs';
import { StaticModal } from '../../StaticModal/StaticModal.mjs';
import { StyleableSvgImage } from '../../StyleableSvgImage/StyleableSvgImage.mjs';
import CloseIcon__default from "../../../assets/close2.svg";
"use strict";
const LanguageFrame = ({
  onLanguageChange,
  open,
  onClose
}) => {
  const {
    deployedLanguages,
    language,
    storeId
  } = useMenu();
  const ref = useRef(null);
  const name = useRef(null);
  const isHovering = useHoverDirty(name);
  const [, setShowPopOver] = useState(false);
  useEffect(() => {
    if (name.current) {
      const displayPopover = name.current.offsetWidth < name.current.scrollWidth;
      if (displayPopover && isHovering) {
        setShowPopOver(true);
      } else if (displayPopover && !isHovering) {
        setShowPopOver(false);
      }
    }
  }, [isHovering]);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  useClickAway(ref, event => {
    let canClose = true;
    if (event.target instanceof Element) {
      const oneTrustElId = "onetrust-banner-sdk";
      const isOneTrustEl = event.target.id === oneTrustElId || !!event.target.closest(`#${oneTrustElId}`);
      if (isOneTrustEl) {
        canClose = false;
      }
    }
    if (canClose) {
      handleClose();
    }
  }, ["click"]);
  const {
    formatMessage
  } = useTranslation({
    defaultTranslation: EN_TRANSLATION,
    additionalTranslations: {
      [Language.Ar]: () => import('./translations/ar.json.mjs').then(res => res.default),
      [Language.Ca]: () => import('./translations/ca.json.mjs').then(res => res.default),
      [Language.Cs]: () => import('./translations/cs.json.mjs').then(res => res.default),
      [Language.Da]: () => import('./translations/da.json.mjs').then(res => res.default),
      [Language.Nl]: () => import('./translations/nl.json.mjs').then(res => res.default),
      [Language.Fr]: () => import('./translations/fr.json.mjs').then(res => res.default),
      [Language.De]: () => import('./translations/de.json.mjs').then(res => res.default),
      [Language.El]: () => import('./translations/el.json.mjs').then(res => res.default),
      [Language.It]: () => import('./translations/it.json.mjs').then(res => res.default),
      [Language.Ja]: () => import('./translations/ja.json.mjs').then(res => res.default),
      [Language.Ko]: () => import('./translations/ko.json.mjs').then(res => res.default),
      [Language.No]: () => import('./translations/no.json.mjs').then(res => res.default),
      [Language.Pl]: () => import('./translations/pl.json.mjs').then(res => res.default),
      [Language.PtPt]: () => import('./translations/pt-PT.json.mjs').then(res => res.default),
      [Language.EsMx]: () => import('./translations/es-MX.json.mjs').then(res => res.default),
      [Language.Sv]: () => import('./translations/sv.json.mjs').then(res => res.default),
      [Language.Tr]: () => import('./translations/tr.json.mjs').then(res => res.default),
      [Language.Zh]: () => import('./translations/zh-Hans.json.mjs').then(res => res.default),
      [Language.Tw]: () => import('./translations/zh-Hant-TW.json.mjs').then(res => res.default),
      [Language.PtBr]: () => import('./translations/pt-BR.json.mjs').then(res => res.default),
      [Language.Ru]: () => import('./translations/ru.json.mjs').then(res => res.default),
      [Language.EsEs]: () => import('./translations/es-ES.json.mjs').then(res => res.default)
    }
  });
  return /* @__PURE__ */jsx(StaticModal, {
    show: !!open,
    width: 754,
    minHeight: 332,
    children: /* @__PURE__ */jsxs(Frame, {
      ref,
      children: [/* @__PURE__ */jsxs(HeadingContainer, {
        children: [/* @__PURE__ */jsx(Heading, {
          children: formatMessage("Select Language")
        }), /* @__PURE__ */jsx(Close, {
          onClick: () => handleClose(),
          children: /* @__PURE__ */jsx(StyleableSvgImage, {
            src: CloseIcon__default,
            width: 10.2,
            height: 10.2
          })
        })]
      }), /* @__PURE__ */jsx(LanguageGrid, {
        children: Object.entries(AvailableLanguages).map(([key, val]) => {
          if (deployedLanguages.includes(key)) {
            const href = buildLanguageLink(storeId, key);
            if (!href) return null;
            return /* @__PURE__ */jsx(CustomLinkButton, {
              anchor: true,
              $active: key === language,
              variant: "link",
              href,
              onClick: e => {
                onLanguageChange?.(e, href, key);
                handleClose();
              },
              "data-test-id": `language-wrap${key === language ? "-active" : ""}`,
              children: /* @__PURE__ */jsx(LanguageValue, {
                "data-test-id": "language-value",
                children: EN_TRANSLATION[val]
              })
            }, key);
          } else {
            return null;
          }
        })
      })]
    })
  });
};
export { LanguageFrame };
//# sourceMappingURL=LanguageFrame.mjs.map
